import React, { memo, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { MARKET_STATUSES, TRADE_METHODS } from 'shared-modules/constants';
import { calculateSpread } from 'shared-modules/services';
import { useInstrumentSettings } from 'shared-modules/services/hooks';
import { getRealInstrumentId } from 'shared-modules/utils';
import EtfInstrumentIcon from '../../../../components/EtfInstrumentIcon';
import ValueCompared from './components/ValueCompared';
import styles from './currencyPairItemETF.module.scss';
import { CustomFlag } from '../../../../components';

const CurrencyPairItemETF = ({
  instrumentId,
  innerRef,
  draggableProps,
  disabled,
  changeActiveCurrency,
  tradeMethod,
}) => {
  const { bid, previousBid, ask, previousAsk } = useSelector((state) => state.currencies.rates[instrumentId]) || {};
  const instrumentList = useSelector((state) => state.settings.instrumentList);
  const { shortName, pricePrecision, symbol } = instrumentList[instrumentId];
  const { marketStatus } = useInstrumentSettings(instrumentId, tradeMethod);
  const isClosed = useMemo(() => Number(marketStatus) === MARKET_STATUSES.CLOSED.ID, [marketStatus]);
  const [firstFlagIcon, secondFlagIcon] = useMemo(() => {
    return getRealInstrumentId(instrumentId).split('/');
  }, [instrumentId]);
  const spread = useMemo(
    () => calculateSpread({ buyPrice: ask, sellPrice: bid, instrumentId, instrumentList }),
    [instrumentId, instrumentList, ask, bid],
  );
  const handleSelectItem = useCallback(() => {
    if (disabled) return false;
    return changeActiveCurrency(instrumentId);
  }, [instrumentId, disabled, changeActiveCurrency]);

  return (
    <div
      role="button"
      aria-hidden
      className={classNames(disabled ? styles.wrapperDisabled : styles.wrapper, { [styles.isDisabled]: disabled })}
      ref={innerRef}
      {...draggableProps.draggableProps} // eslint-disable-line
      onClick={handleSelectItem}
    >
      <i
        className={classNames('material-icons', styles.draggableIcon)}
        {...draggableProps.dragHandleProps} // eslint-disable-line
      >
        drag_indicator
      </i>
      {disabled ? (
        <>
          <div className={styles.currencyBlockDisabled}>
            <div className={styles.flagContainerDisabled}>
              <CustomFlag country={firstFlagIcon} className={styles.flagDisabled} />
              <CustomFlag country={secondFlagIcon} className={styles.flagDisabled} />
            </div>

            <div className={classNames(styles.currencyPairRowDisabled, { [styles.isClosed]: isClosed || disabled })}>
              {shortName}
            </div>
          </div>
          <ValueCompared
            instrumentId={instrumentId}
            value={bid}
            prevValue={previousBid}
            precision={pricePrecision}
            isClosed={isClosed || disabled}
          />
          <div className={classNames(styles.spread, { [styles.isClosed]: isClosed || disabled })}>{spread}</div>
          <ValueCompared
            instrumentId={instrumentId}
            value={ask}
            prevValue={previousAsk}
            precision={pricePrecision}
            isClosed={isClosed || disabled}
          />
        </>
      ) : (
        <>
          <div className={styles.currencyBlock}>
            <div className={styles.flagContainer}>
              <EtfInstrumentIcon instrumentId={instrumentId} size="small" />
            </div>
          </div>
          <div className={styles.valuesInner}>
            <div className={styles.currencyPairRow}>
              <p className={styles.instrumentName}>{shortName}</p>
              <p className={styles.symbol}>{symbol}</p>
            </div>
            <div className={styles.valuesRow}>
              <ValueCompared value={bid} prevValue={previousBid} precision={pricePrecision} isClosed={isClosed} />
              <div className={classNames(styles.spread, { [styles.isClosed]: isClosed })}>
                {calculateSpread({
                  buyPrice: ask,
                  sellPrice: bid,
                  instrumentId,
                  instrumentList,
                })}
              </div>
              <ValueCompared value={ask} prevValue={previousAsk} precision={pricePrecision} isClosed={isClosed} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

CurrencyPairItemETF.propTypes = {
  instrumentId: PropTypes.string.isRequired,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.shape({}) })]).isRequired,
  draggableProps: PropTypes.shape({}).isRequired,
  changeActiveCurrency: PropTypes.func,
  disabled: PropTypes.bool,
  tradeMethod: PropTypes.number,
};

CurrencyPairItemETF.defaultProps = {
  changeActiveCurrency: () => {},
  disabled: false,
  tradeMethod: TRADE_METHODS.MANUAL.ID,
};

export default memo(CurrencyPairItemETF);
